import { ApexOptions } from 'apexcharts';
import constants from 'constants/constants';
import moment from 'moment-timezone';
import colorsVars from 'styles/colors.module.scss';
import { addTestIdsToElements } from 'utilitys/helpers/general';

import { QueryResponse } from 'api/queries/productivityRate.query';

import { Range } from 'components/common/GlobalFiltersInPage/components/RangeFilter/rangeFilter';

const {
    runratePastPlannedLight,
    runratePastActualLight,
    runrateFuturePlannedLight,
    runrateFutureActualLight,
    runratePastPlannedDark,
    runratePastActualDark,
    runrateFuturePlannedDark,
    runrateFutureActualDark,
} = colorsVars;

export const categoriesKeys = [
    {
        key: 'previousPlanned',
        label: 'Past Planned',
        color: {
            light: runratePastPlannedLight,
            dark: runratePastPlannedDark,
        },
    },
    {
        key: 'previousActual',
        label: 'Past Actual',
        color: {
            light: runratePastActualLight,
            dark: runratePastActualDark,
        },
    },
    {
        key: 'futurePlanned',
        label: 'Future Planned',
        color: {
            light: runrateFuturePlannedLight,
            dark: runrateFuturePlannedDark,
        },
    },
    {
        key: 'futureActual',
        label: 'Future Actual',
        color: {
            light: runrateFutureActualLight,
            dark: runrateFutureActualDark,
        },
    },
];

const rangeDailyOptions: Range[] = ['R_2W', 'R_4W', 'R_3M', 'R_6M'];

export const getFormat = ({
    data,
    range,
}: {
    data: { startDate: number; endDate: number }[];
    range: Range | number[];
}) => {
    const WEEKS_FORMAT = 'DD MMM YYYY';
    const MONTHS_FORMAT = 'MMM YYYY';

    if (Array.isArray(range)) {
        const startDate = moment.utc(data[0].startDate);
        const endDate = moment.utc(data[data.length - 1].endDate);
        if (endDate.diff(startDate, 'years', true) < 0.6) {
            // if end minus start is less than a year? weeks : months
            return WEEKS_FORMAT;
        } else {
            return MONTHS_FORMAT;
        }
    } else {
        return rangeDailyOptions.includes(range) ? WEEKS_FORMAT : MONTHS_FORMAT;
    }
};

export const serialize = ({
    data,
    range,
}: {
    data: QueryResponse['chart'] | undefined;
    range: Range | undefined;
}): { categories: string[]; series: ApexAxisChartSeries | ApexNonAxisChartSeries } => {
    if (!data || !range)
        return {
            categories: [],
            series: [],
        };
    const format = getFormat({ data, range });
    const categories = data.map((item) => {
        return moment(item.startDate).format(format);
    });
    const series = categoriesKeys.map((item) => {
        return {
            name: item.label,
            data: data.map((d) => d[item.key]),
        };
    });
    return {
        categories,
        series,
    };
};

interface Props {
    series: ApexAxisChartSeries | ApexNonAxisChartSeries;
    categories: string[];
    themeStatus: boolean;
    dataPointSelection: (data) => void;
    versionDate: number | null | undefined;
    tooltipFormatter: (data) => string;
    actualKpi: number | undefined;
}

export const getChartConfig = ({
    series,
    categories,
    themeStatus,
    dataPointSelection,
    versionDate,
    tooltipFormatter,
    actualKpi,
}: Props): { options: ApexOptions; series: ApexOptions['series'] } => ({
    options: {
        colors: categoriesKeys.map((item) => (themeStatus ? item.color.light : item.color.dark)),
        dataLabels: {
            enabled: false,
        },
        plotOptions: {
            bar: {
                columnWidth: '100%',
            },
        },
        states: {
            active: {
                filter: {
                    type: 'darken',
                },
            },
            hover: {
                filter: {
                    type: 'darken',
                },
            },
        },
        tooltip: {
            x: {
                formatter: function (_value, { dataPointIndex }) {
                    return tooltipFormatter({ dataPointIndex });
                },
            },
            style: {
                fontFamily: 'Roboto',
                fontSize: '14px',
            },
        },
        grid: {
            borderColor: 'var(--border-secondary)',
        },
        chart: {
            background: themeStatus ? 'transparent' : '#ffffff',
            foreColor: themeStatus ? '#FEFEFE' : '#000000',
            toolbar: {
                tools: {
                    download: false,
                    selection: false,
                    zoom: true,
                    zoomin: true,
                    zoomout: true,
                    pan: false,
                    reset: true,
                },
            },
            events: {
                dataPointSelection: (_event, _chartContext, options) => {
                    dataPointSelection(options);
                },

                beforeZoom(chart: any) {
                    if (chart.wheelDelay) {
                        return {
                            xaxis: {
                                min: chart.minX,
                                max: chart.maxX,
                            },
                        };
                    }
                    return undefined;
                },
                updated: () => {
                    addTestIdsToElements('.apexcharts-bar-series .apexcharts-series path', 'run-rate-chart');
                },
            },
        },
        xaxis: {
            type: 'datetime',
            categories,
            labels: {
                datetimeUTC: false,
                datetimeFormatter: {
                    year: 'yyyy',
                    month: 'MMM yyyy',
                    day: 'dd MMM',
                },
                style: {
                    fontFamily: 'Roboto',
                    fontSize: '14px',
                },
            },
        },
        annotations: {
            xaxis: [
                {
                    x: versionDate,
                    borderWidth: 2,
                    strokeDashArray: 0,
                    borderColor: '#775DD0',
                    label: {
                        offsetY: 50,
                        borderColor: '#775DD0',
                        orientation: 'horizontal',
                        style: {
                            color: '#fff',
                            background: '#775DD0',
                        },
                        text: moment(versionDate).format(constants.formats.date.default),
                    },
                },
            ],
            yaxis: [
                {
                    y: actualKpi,
                    width: '100%',
                    strokeDashArray: 5,
                    borderColor: themeStatus ? runratePastActualLight : runratePastActualDark,
                    borderWidth: 3,
                    label: {
                        position: 'right',
                        borderColor: 'transparent',
                        offsetY: -5,
                        text: `Past Actual (${actualKpi} units)`,
                        style: {
                            color: themeStatus ? runratePastActualLight : runratePastActualDark,
                            fontFamily: 'Roboto',
                            fontSize: '16px',
                        },
                    },
                },
            ],
        },
        legend: {
            fontSize: '14px',
            fontFamily: 'Roboto',
        },
    },

    series,
});
